// The town square's backdrop, cross-fading with the phase: a day scene for day and setup, and one
// of five night scenes. Two stacked layers make the swap a dissolve, and images are preloaded so a
// first use doesn't pop in mid-fade.

import { useEffect, useRef, useState } from "react";
import type { Phase } from "@cc/shared";

const FILES = ["daytime.jpg", "night1.jpg", "night2.jpg", "night3.jpg", "night4.jpg", "night5.jpg"];

let warmed = false;
function warmCache(): void {
  if (warmed) return;
  warmed = true;
  for (const file of FILES) {
    const img = new Image();
    img.src = `/photos/${file}`;
  }
}

function backgroundFor(phase: Phase): string {
  const isNight = phase.kind === "night";
  const tint = isNight ? "rgba(4,4,10,0.68)" : "rgba(8,8,8,0.5)";
  const file = isNight ? `night${Math.min(Math.max(phase.number, 1), 5)}.jpg` : "daytime.jpg";
  return `linear-gradient(${tint}, ${tint}), url("/photos/${file}")`;
}

export function Backdrop({ phase }: { phase: Phase }) {
  const [layers, setLayers] = useState<[string | null, string | null]>([null, null]);
  const [active, setActive] = useState<0 | 1>(0);
  const current = useRef<string | null>(null);

  useEffect(() => warmCache(), []);

  useEffect(() => {
    const next = backgroundFor(phase);
    if (next === current.current) return;
    current.current = next;
    const target = active === 0 ? 1 : 0;
    setLayers((prev) => (target === 0 ? [next, prev[1]] : [prev[0], next]));
    setActive(target);
  }, [phase, active]);

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="cc-backdrop-layer"
          data-active={active === i && layers[i] !== null}
          style={layers[i] ? { backgroundImage: layers[i]! } : undefined}
        />
      ))}
    </div>
  );
}
